import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText, Icon, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import DialogBase from './DialogBase';
import { Task } from '../../../models/portfolio/task';
import { contentService } from '../../../services/content.service';
import { DateTimePicker } from '@mui/x-date-pickers';

import dayjs from "dayjs";
import "dayjs/locale/nl"; // Import Dutch locale
import { Annotation } from '../../../models/portfolio/annotation';


interface DialogAnnotationAddProps {
  open: boolean;
  selectedAnnotation?: Annotation;
  portfolioId?: string;
  parentId?: string;
  onClose: () => void;
  onSubmit: (beoordeling: any) => void;
}

const MAX_LENGTH = 2000;
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const DialogAnnotationAdd: React.FC<DialogAnnotationAddProps> = ({
  open,
  selectedAnnotation,
  portfolioId,
  parentId,
  onClose,
  onSubmit
}) => {
  const [annotation, setAnnotation] = useState<Annotation>({} as Annotation);
  const [charCount, setCharCount] = useState(annotation?.description?.length || 0);

  const [validationErrors, setValidationErrors] = useState<{ 
    title?: string;
    description?: string;
  }>({});

  useEffect(() => {
    if (open) {
      if (selectedAnnotation) {
        setAnnotation(selectedAnnotation);
        setCharCount(selectedAnnotation.description?.length || 0);
      } 
      else {
        setAnnotation({ 
          portfolioId: portfolioId, 
          parentAnnotationId: parentId
        } as Annotation);
        setCharCount(0);
      }
      setValidationErrors({});
    }
  }, [open, selectedAnnotation, portfolioId, parentId]);

  const onChange = (key: keyof Task, value: any) => {
    setAnnotation(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const errors: typeof validationErrors = {};


    if (!parentId && (!annotation.title || annotation.title.trim() === '')) errors.title = 'Titel is verplicht';
    if (annotation.description && annotation.description.length > MAX_LENGTH) errors.description = 'Opmerking mag maximaal 2000 tekens bevatten';
    if (!annotation.description || annotation.description.trim() === '') errors.description = 'Opmerking is verplicht';

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // console.log('Submitting annotation:', annotation);
    onSubmit(annotation);
    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={parentId ? 'Reactie toevoegen' : 'Notitie toevoegen'}
      contentText=""
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Opslaan"
      confirmButtonIcon={<Icon>save</Icon>}
      width="1200px"
      minWidth="800px"
    >
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          label="Titel"
          placeholder="Vul een titel in"
          variant="filled"
          value={annotation.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          error={!!validationErrors.title}
          helperText={validationErrors.title}
        />
      </FormControl>

      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          label={parentId ? 'Opmerking' : 'Notitie'}
          placeholder={parentId ? 'Voeg een opmerking toe...' : 'Vul een notitie in...'}
          variant="filled"
          multiline
          rows={10}
          onChange={(e) => {
            onChange('description', e.target.value);
            setCharCount(e.target.value.length);
          }}
          value={annotation.description || ''}
          error={!!validationErrors.description}
          helperText={validationErrors.description}
          inputProps={{ maxLength: MAX_LENGTH }}
        />
        <FormHelperText
          sx={{
            textAlign: 'right',
            fontSize: '0.8rem',
            color: charCount >= MAX_LENGTH ? 'error.main' : 'text.secondary',
            marginRight: 1,
          }}
        >
          {charCount} / {MAX_LENGTH}
        </FormHelperText>
      </FormControl>
    </DialogBase>
  );
};

export default DialogAnnotationAdd;
